import { cn } from "../../lib/utils";
import { AMBER_BORDER, AMBER_TINT } from "./tokens";

/**
 * The coach's per-message avatar.
 *
 * `yami-kuro-head.webp` is a *measured* square crop of the YamiKuro portrait
 * (side 1560 centred at (1024, 800) in the 2048² source — see the design spec
 * §7 asset contract). It ships square with alpha and is **not** pre-masked, so
 * the circular mask and the amber ring live here. Do not add a `transform:
 * scale()` "to fill the circle": the prototype needed one only because it had
 * the uncropped head-and-shoulders portrait, and scaling this asset clips the
 * ear tips the crop was chosen to keep.
 *
 * Decorative. Identity in the transcript is carried by position and shape,
 * and the message text is the accessible content, so a screen reader gains
 * nothing from "image of a cat" on every turn.
 *
 * The user's side of the transcript is `<Avatar>` (`../Avatar.tsx`), not
 * here -- it renders the signed-in user's own uploaded photo, or their
 * monogram, never a hardcoded portrait. See MessageBubble.tsx.
 */

const COACH_SRC = "/images/yami-kuro-head.webp";

interface AvatarProps {
  /** Rendered size in px (square). 36 matches the message list; the dock uses 36 too. */
  size?: number;
  className?: string;
}

export function CoachAvatar({ size = 36, className }: AvatarProps) {
  return (
    <span
      aria-hidden="true"
      className={cn("block shrink-0 overflow-hidden rounded-full", className)}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        background: AMBER_TINT,
        border: `1px solid ${AMBER_BORDER}`,
      }}
    >
      <img
        src={COACH_SRC}
        alt=""
        width={size}
        height={size}
        decoding="async"
        className="h-full w-full"
        style={{ objectFit: "cover", objectPosition: "50% 50%" }}
      />
    </span>
  );
}
